import { useProfile } from "@/hooks";
import { KycStatus, KycType } from "@/interfaces/kyc";
import { cn } from "@/lib/utils";
import { useAppSelector } from "@/store/hooks";
import { useState, type Dispatch, type SetStateAction } from "react";
import { Badge } from "../ui/badge";
import { AlertTriangle, Check, ChevronRight, Clock } from "lucide-react";
import { Button } from "../ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { toast } from "sonner";

type KycLevel = 1 | 2 | 3;

interface Props {
  activeLevel: KycLevel;
  setActiveLevel: Dispatch<SetStateAction<KycLevel>>;
  kycType: KycType;
  setKycType: Dispatch<SetStateAction<KycType>>;
  activeTab: "info" | "documents";
  setActiveTab: Dispatch<SetStateAction<"info" | "documents">>;
}

const VerificationLevel = ({
  activeLevel,
  setActiveLevel,
  kycType,
  setActiveTab,
}: Props) => {
  const [actionModal, setActionModal] = useState<{
    open: boolean;
    level?: KycLevel;
    reason?: string;
  }>({ open: false });

  const { user } = useAppSelector((state) => state.auth);
  const userId = user?.id ?? "";

  const {
    data: profile,
    isLoading: isLoadingProfile,
    isError,
    refetch,
  } = useProfile(userId);

  const getLevelMeta = (level: KycLevel) => {
    const submission = profile?.kycSubmissions.find(
      (k) => k.level === level && k.kycType === kycType,
    );

    if (!submission) {
      return {
        status: KycStatus.DRAFT,
        action: "START",
        label: "Not Started",
      };
    }

    const normalizeStatus = (status: string): KycStatus => {
      switch (status) {
        case KycStatus.NEEDS_MORE_INFO:
          return KycStatus.REJECTED; // treat as actionable rejection state
        default:
          return status as KycStatus;
      }
    };

    const normalizedStatus = normalizeStatus(submission.status);

    switch (submission.status) {
      case KycStatus.NEEDS_MORE_INFO:
        return {
          status: normalizedStatus,
          action: "NEEDS_ATTENTION",
          label: "Action Required",
          reason: submission.reviewNote,
        };

      case KycStatus.UNDER_REVIEW:
        return {
          status: normalizedStatus,
          action: "VIEW",
          label: "Under Review",
        };

      case KycStatus.APPROVED:
        return {
          status: normalizedStatus,
          action: null,
          label: "Completed",
        };

      default:
        return {
          status: normalizedStatus,
          action: "CONTINUE",
          label: "Continue",
        };
    }
  };

  const handleLevelAction = (level: KycLevel) => {
    const meta = getLevelMeta(level);

    const submission = profile?.kycSubmissions.find(
      (k) => k.level === level && k.kycType === kycType,
    );

    setActiveLevel(level);

    // 1. ACTION REQUIRED → OPEN MODAL (THIS WAS MISSING)
    if (meta.action === "NEEDS_ATTENTION") {
      setActionModal({
        open: true,
        level,
        reason: submission?.reviewNote || "Please update required documents.",
      });
      return;
    }

    // 2. CONTINUE FLOW
    if (meta.action === "CONTINUE") {
      setActiveTab("info");

      toast.info("Continuing KYC", {
        description:
          submission?.status === KycStatus.NEEDS_MORE_INFO
            ? "Please update requested documents"
            : "You can continue your verification.",
      });

      // IMPORTANT UX: force user to documents if admin requested more info
      if (submission?.status === "NEEDS_MORE_INFO") {
        setTimeout(() => {
          setActiveTab("documents");
        }, 50);
      }

      return;
    }

    // 3. VIEW ONLY
    if (meta.action === "VIEW") {
      setActiveTab("info");
      return;
    }

    // 4. START NEW
    if (meta.action === "START") {
      setActiveTab("info");
      return;
    }
  };

  const kycLevels = {
    PERSONAL: [
      {
        level: 1,
        name: "Basic Verification",
        limit: "$1,000/day",
        benefits: ["Basic trading", "Email support", "Crypto deposits"],
        requirements: ["Government ID", "Selfie verification"],
      },
      {
        level: 2,
        name: "Verified",
        limit: "$10,000/day",
        benefits: [
          "Higher limits",
          "Priority support",
          "P2P trading",
          "Fiat deposits",
        ],
        requirements: ["Proof of address"],
      },
      {
        level: 3,
        name: "Advanced",
        limit: "Unlimited",
        benefits: [
          "Unlimited trading",
          "VIP support",
          "API access",
          "OTC trading",
        ],
        requirements: ["Source of funds", "Bank reference", "Income proof"],
      },
    ],
    BUSINESS: [
      {
        level: 1,
        name: "Business Basic",
        limit: "$5,000/day",
        benefits: ["Business account", "Multi-user access", "Basic trading"],
        requirements: ["Business registration", "Director ID"],
      },
      {
        level: 2,
        name: "Business Verified",
        limit: "$50,000/day",
        benefits: [
          "Higher limits",
          "Priority support",
          "P2P trading",
          "Vendor features",
        ],
        requirements: ["Tax clearance", "Bank statement"],
      },
      {
        level: 3,
        name: "Business Enterprise",
        limit: "Unlimited",
        benefits: [
          "Unlimited trading",
          "Dedicated manager",
          "API access",
          "Custom solutions",
        ],
        requirements: ["Financial statements", "Trade license"],
      },
    ],
  };

  const getKycStatusColor = (status: KycStatus) => {
    switch (status) {
      case KycStatus.APPROVED:
        return "bg-emerald-500/10 text-emerald-500 border-emerald-500/30";

      case KycStatus.UNDER_REVIEW:
        return "bg-blue-500/10 text-blue-500 border-blue-500/30";

      case KycStatus.REJECTED:
        return "bg-red-500/10 text-red-500 border-red-500/30";

      case KycStatus.DRAFT:
        return "bg-muted text-muted-foreground";

      default:
        return "bg-muted text-muted-foreground";
    }
  };

  return (
    <div>
      <div className="lg:col-span-1 space-y-4">
        <h3 className="font-semibold text-foreground">Verification Levels</h3>
        {kycLevels[kycType].map((level: any) => {
          const submission = profile?.kycSubmissions.find(
            (k) => k.level === level.level && k.kycType === kycType,
          );

          const meta = getLevelMeta(level.level as KycLevel);
          return (
            <div
              key={level.level}
              onClick={() => setActiveLevel(level.level as KycLevel)}
              className={cn(
                "w-full text-left p-4 rounded-xl border transition-all flex justify-between items-center",
                activeLevel === level.level
                  ? "border-primary bg-primary/5"
                  : "border-border hover:border-primary/30",
              )}
            >
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center font-bold">
                  {level.level}
                </div>
                <div>
                  <p className="font-medium">{level.name}</p>
                  <p className="text-xs text-muted-foreground">
                    Limit: {level.limit}
                  </p>
                  {submission && (
                    <p className="text-xs text-muted-foreground mt-1">
                      Status:{" "}
                      <span className="font-medium">
                        {submission.status === "NEEDS_MORE_INFO"
                          ? "Action Required"
                          : submission.status.replace("_", " ")}
                      </span>
                    </p>
                  )}
                </div>
              </div>
              <Badge className={cn("border", getKycStatusColor(meta.status))}>
                {meta.status === KycStatus.APPROVED && (
                  <Check className="w-3 h-3 mr-1" />
                )}
                {meta.status === KycStatus.UNDER_REVIEW && (
                  <Clock className="w-3 h-3 mr-1" />
                )}
                {meta.status === KycStatus.REJECTED && (
                  <AlertTriangle className="w-3 h-3 mr-1" />
                )}

                {meta.label}
              </Badge>

              {meta.action && (
                <Button
                  size="sm"
                  variant="ghost"
                  className="ml-2"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleLevelAction(level.level as KycLevel);
                  }}
                >
                  {meta.action === "FIX" && "Fix Issues"}
                  {meta.action === "CONTINUE" && "Continue"}
                  {meta.action === "VIEW" && "View"}
                  {meta.action === "START" && "Start"}
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              )}
            </div>
          );
        })}
      </div>

      <Dialog
        open={actionModal.open}
        onOpenChange={(open) => setActionModal({ open })}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-500" />
              Action Required
            </DialogTitle>
            <DialogDescription>
              Your submission needs correction before approval.
            </DialogDescription>
          </DialogHeader>

          <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4 text-sm">
            {actionModal.reason}
          </div>

          <div className="flex gap-2 mt-4">
            <Button
              className="flex-1"
              onClick={() => {
                setActiveLevel(actionModal.level!);
                setActiveTab("documents");
                setActionModal({ open: false });
              }}
            >
              Fix Now
            </Button>

            <Button
              variant="outline"
              className="flex-1"
              onClick={() => setActionModal({ open: false })}
            >
              Cancel
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default VerificationLevel;
